import * as React from "react";
import { useCallback, useState } from "react";
import styled from "@mui/material/styles/styled";
import { ClinicDashBoardRoute } from "../../clinic/ClinicRoutes";
import { ReactComponent as KahunLogo } from "../../assets/kahunLogoNew.svg";
import { ReactComponent as ManageUsersIcon } from "../../assets/clinic/manageUsersMenuIcon.svg";
import TranslateIcon from "@mui/icons-material/Translate";
import { ClinicMenuButton, ClinicMenuItem } from "./clinicMenuItem";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useClinicState } from "../../clinic/clinicStateContext";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { ClinicEnvQuickLinks } from "./menu/clinicEnvQuickLinks";
import { AddClinicButton } from "./uiTemplates/addClinicButton";
import { Link } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { ColorLens } from "@mui/icons-material";
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { ClinicNameBox } from "./menu/clinicNameBox";
import ArchiveIcon from "@mui/icons-material/Archive";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LinkIcon from "@mui/icons-material/Link";
import { CaseGenerator } from "../CaseGenerator";
import { useClinicUserRoles } from "./clinicUsers/useClinicUserRoles";
import { CLINIC_MANAGE_URL } from "./clinicSettings/clinicUrls";
//region [[ Styles ]]

const ClinicMenuView = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  justifyContent: "start",
  flex: 1,
  zIndex: 2,
});
const MenuTop = styled("div")({
  margin: "20px 0 40px 0",
});
const ClinicLogo = styled(KahunLogo)({
  with: 115,
  height: 35,
});

const ClinicMenuContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  width: "100%",
  height: "100%",
});

const ArchiveButton = styled(Button)({
  color: "#9d6464",
  borderColor: "#9d6464",
  width: "40%",
  margin: "0 30%",
  "&:hover": {
    color: "red",
    borderColor: "red",
  },
  display: "flex",
  flexFlow: "row",
  alignItems: "space-between",
});

const ClinicSelectorView = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
});

const PrivateModeSwitch = styled(FormControlLabel)({
  marginTop: "auto",
  marginLeft: 0,
  marginRight: 0,
  gap: 4,
  "& .MuiFormControlLabel-label": {
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    gap: 6,
    lineHeight: 1,
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

//endregion [[ Props ]]
const LINK_GENERATOR_CLINIC_IDS = [
  "40ce2cb2-28fb-4a52-b1c4-0ff20375f741",
  "780f0c61-aa31-4fba-81f1-6740c76b768c", //Cleveland
  "1f91c057-cfe0-451b-84aa-2b6110e104f0", //gkehatTest
];
//region [[ Functions ]]
//endregion [[ Functions ]]

export const ClinicMenu = (props: { onClinicSelect: () => void }) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const dao = useClinicContext();
  const [
    {
      isMasterClinic,
      clinicData: { name: clinicName, id },
      privateMode,
    },
    setClinicState,
  ] = useClinicState();
  const { loading, hasManagerAccess, hasAdminAccess } = useClinicUserRoles(id);
  const [showGeneratedLink, setShowGeneratedLink] = useState<boolean>(false);
  const [archivePopUp, setArchivePopUp] = useState(false);

  const history = useHistory();

  const onArchive = useCallback(async () => {
    if (isMasterClinic) return; //cannot archive master clinic
    setDeleteLoading(true);
    const status = await dao.deleteClinic(id);
    setDeleteLoading(false);

    if (status === "success") {
      setArchivePopUp(false);
      history.push(CLINIC_MANAGE_URL);
      window.location.reload();
    }
  }, [dao, id, isMasterClinic, history]);

  const linkGenerateButton = (
    <ClinicMenuButton
      label="Generate Case Link"
      icon={<LinkIcon />}
      onClick={() => setShowGeneratedLink(true)}
    />
  );

  const togglePrivateMode = () => {
    setClinicState((prevState) => ({
      ...prevState,
      privateMode: !prevState.privateMode,
    }));
  };

  if (loading) return <CircularProgress />;

  return (
    <ClinicMenuView>
      <MenuTop>
        <Link to={CLINIC_MANAGE_URL}>
          <ClinicLogo />
        </Link>
      </MenuTop>
      {hasAdminAccess && (
        <>
          <ClinicSelectorView>
            <ClinicNameBox onClinicSelect={props.onClinicSelect} />
            <AddClinicButton />
          </ClinicSelectorView>
          <ClinicEnvQuickLinks />
        </>
      )}
      <ClinicMenuContainer>
        {!isMasterClinic && hasManagerAccess && (
          <ClinicMenuItem
            label={"Patients Dashboard"}
            route={ClinicDashBoardRoute.Patients}
          />
        )}
        {!isMasterClinic && (
          <ClinicMenuItem
            label={"Invite Patients"}
            route={ClinicDashBoardRoute.Invite}
          />
        )}

        <ClinicMenuItem
          label={"Settings"}
          route={ClinicDashBoardRoute.Settings}
        />

        {!isMasterClinic &&
          (LINK_GENERATOR_CLINIC_IDS.includes(id) || hasAdminAccess) &&
          linkGenerateButton}
        {hasAdminAccess && (
          <ClinicMenuItem
            icon={<ManageUsersIcon />}
            label={"Advanced Settings"}
            route={ClinicDashBoardRoute.Advanced}
          />
        )}
        {hasAdminAccess && (
          <ClinicMenuItem
            icon={<ColorLens />}
            label={"Theme Color Editor"}
            route={ClinicDashBoardRoute.ThemeColors}
          />
        )}
        {hasAdminAccess && (
          <ClinicMenuItem
            icon={<TranslateIcon />}
            label={"Text Overrides"}
            route={ClinicDashBoardRoute.Translation}
          />
        )}
        {hasAdminAccess && (
            <ClinicMenuItem
                icon={<AssessmentOutlinedIcon />}
                label={"Reports"}
                route={ClinicDashBoardRoute.Reports}
            />
        )}
        {hasManagerAccess && id === "17e0ab99-1555-4cc0-8fd5-15b1c0fdad08" && (
          <ClinicMenuItem
            icon={<ExitToAppIcon />}
            label={"Import Maccabi Patient Data"}
            route={ClinicDashBoardRoute.ImportPatients}
          />
        )}
        {hasAdminAccess && !isMasterClinic && (
          <ArchiveButton
            onClick={() => setArchivePopUp(true)}
            variant="outlined"
          >
            <ArchiveIcon style={{ margin: "0 10px" }} />
            archive
          </ArchiveButton>
        )}
        {archivePopUp && (
          <Dialog open={true} onClose={() => setArchivePopUp(false)}>
            <DialogTitle>
              {"Are you sure you want to archive " + clinicName + "?"}
              {deleteLoading && <CircularProgress />}
            </DialogTitle>
            <DialogActions>
              <Button
                disabled={deleteLoading}
                onClick={() => setArchivePopUp(false)}
              >
                Disagree
              </Button>
              <Button disabled={deleteLoading} onClick={onArchive} autoFocus>
                Agree
              </Button>
            </DialogActions>
          </Dialog>
        )}
        <PrivateModeSwitch
          control={
            <Switch
              checked={privateMode}
              onChange={togglePrivateMode}
              name="privateMode"
              color="primary"
              size={"small"}
            />
          }
          label={
            <>
              <i className="fal fa-user-secret"></i>
              Hide patients private information
            </>
          }
        />
      </ClinicMenuContainer>
      {showGeneratedLink && (
        <CaseGenerator
          clinicName={clinicName}
          clinicId={id}
          onClose={() => setShowGeneratedLink(false)}
          autoHideCopied={false}
        />
      )}
    </ClinicMenuView>
  );
};
